import { OmsOrderState, PrivateMessage, PrivateMessageAction } from '../../core/private-messages/private-message.model';

/**
 * Application-level events raised from the `private` SSE topic.
 *
 * These are the whole of what a consumer needs to know: nothing downstream of
 * the event bus should be able to tell that the messages arrived over SSE, let
 * alone which upstream action name produced them.
 */
export const PRIVATE_MESSAGE_EVENTS = {
	/** Every private message that parsed, whatever it turned out to be. */
	RECEIVED: 'private-message.received',
	/** Persian prose meant for the user. */
	TEXT_RECEIVED: 'private-message.text-received',
	/** An order changed state. */
	ORDER_STATE_CHANGED: 'private-message.order-state-changed',
	/** The order list is stale — refetch it. */
	ORDERS_RELOAD_REQUESTED: 'private-message.orders-reload-requested',
	/** Balances / remaining credit are stale — refetch them. */
	REMAIN_RELOAD_REQUESTED: 'private-message.remain-reload-requested',
	/** Deposit holdings are stale — refetch them. */
	DEPOSIT_PORTFOLIO_RELOAD_REQUESTED: 'private-message.deposit-portfolio-reload-requested'
} as const;

/** Payload of {@link PRIVATE_MESSAGE_EVENTS.RECEIVED}. */
export type PrivateMessageReceivedEvent = PrivateMessage;

/** Payload of {@link PRIVATE_MESSAGE_EVENTS.TEXT_RECEIVED}. */
export interface PrivateMessageTextEvent {
	/** The text to display. Never parse it to decide whether something succeeded. */
	readonly text: string;
	/** Which text channel it came from: about an order (`text`) or the account (`Text_IME`). */
	readonly kind: 'text' | 'Text_IME';
	/** The upstream `type` field, carried through because its meaning is not yet known. */
	readonly type: string;
	/** `{brokerId}_{userName}` — stable per user, not a message id. */
	readonly itemKey: string;
}

/** Payload of {@link PRIVATE_MESSAGE_EVENTS.ORDER_STATE_CHANGED}. */
export type PrivateMessageOrderStateEvent = OmsOrderState;

/** Payload of the two reload events. */
export interface PrivateMessageReloadEvent {
	/**
	 * Why the refetch was asked for: the upstream action name, or `'reconnect'`
	 * when the stream came back and may have missed messages while it was down.
	 *
	 * Informational only — a consumer reloads the same way whatever this says.
	 */
	readonly reason: PrivateMessageAction | 'reconnect';
}
